import { useState, useRef, useCallback } from 'react';
import { mediaApi } from 'api';
import { useChat } from '../components/context/ChatContext';
import { toast } from 'react-hot-toast';
import { GOOGLE_MAX_FILE_SIZE, META_MAX_FILE_SIZE, MB } from 'constants/utils';

const useMedia = (campaignId, provider, onRefetchCampaign) => {
  const [loadingLogo, setLoadingLogo] = useState(false);
  const [loadingMedia, setLoadingMedia] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [logo, setLogo] = useState(null);
  const logoInputRef = useRef(null);
  const mediaInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [mediaErrors, setMediaErrors] = useState({});
  const [viewMode, setViewMode] = useState('grid');
  const [error, setError] = useState(null);
  const { fetchCampaign } = useChat();

  const getLogo = useCallback(async () => {
    setLoadingLogo(true);
    try {
      const response = await mediaApi.getLogo(campaignId);
      const logoData = response.data.logo ? {
        id: response.data.logo.id,
        url: response.data.logo.url,
        type: 'logo'
      } : null;

      setLogo(logoData);
      return logoData;
    } catch (err) {
      setError(`Error al obtener el logo: ${err.message}`);
    } finally {
      setLoadingLogo(false);
    }
  }, [campaignId]);

  const getMedia = useCallback(async () => {
    setLoadingMedia(true);
    try {
      const response = await mediaApi.getMedia(campaignId);
      const media = response.data.media.map(media => ({
        id: media.id,
        url: media.url,
        type: 'generic'
      }));

      setMediaFiles(media);
      return media;
    } catch (err) {
      setError(`Error al obtener los medios de la campaña: ${err.message}`);
    } finally {
      setLoadingMedia(false);
    }
  }, [campaignId]);

  const getAllMedia = useCallback(async () => {
    setLoadingLogo(true);
    setLoadingMedia(true);
    try {
      await Promise.all([getLogo(), getMedia()]);
    } catch (err) {
      setError(`Error al obtener los medios: ${err.message}`);
    } finally {
      setLoadingLogo(false);
      setLoadingMedia(false);
    }
  }, [getLogo, getMedia]);

  const handleLogoUpload = async (file) => {
    if (!file || loadingLogo) return;
  
    setLoadingLogo(true);
  
    try {
      const isImage = file.type.startsWith('image/');

      if (!isImage) {
        toast.error('El logo debe ser una imagen');
        return;
      }

      // Validate file size
      if (provider === 'google' && file.size > GOOGLE_MAX_FILE_SIZE) {
          toast.error(`El logo no debe superar los ${GOOGLE_MAX_FILE_SIZE / MB} MB`);
          return;
      } else if (provider === 'meta' && file.size > META_MAX_FILE_SIZE) {
        toast.error(`El logo no debe superar los ${META_MAX_FILE_SIZE / MB} MB`);
        return;
      }

      // Validate square aspect ratio
      const img = new Image();
      await new Promise((resolve, reject) => {
        img.onload = () => {
          const isSquare = Math.abs(img.width - img.height) < 1;
          if (!isSquare) {
            reject('El logo debe ser cuadrado');
          } else {
            resolve();
          }
        };
        img.onerror = () => reject('Error al cargar la imagen');
        img.src = URL.createObjectURL(file);
      });

      await mediaApi.uploadLogo(campaignId, file);
      toast.success('Logo subido correctamente');
      
      // Update state directly instead of calling getLogo()
      const logoData = {
        id: 'new-logo-id', // This will be updated when the campaign is fetched
        url: URL.createObjectURL(file),
        type: 'logo'
      };
      setLogo(logoData);
      
      // Fetch updated campaign after logo upload
      if (onRefetchCampaign) {
        await onRefetchCampaign();
      } else {
        const chatId = new URLSearchParams(window.location.search).get('chat');
        if (chatId) {
          await fetchCampaign(chatId);
        }
      }
    } catch (error) {
      const errorMessage = typeof error === 'string' ? error : error.response?.data?.error || error.message;
      toast.error(`Error al subir el logo: ${errorMessage}`);
    } finally {
      setLoadingLogo(false);
    }
  };

  const handleMediaUpload = async (files) => {
    if (!files || files.length === 0 || uploadingMedia) return;
  
    setUploadingMedia(true);
  
    try {
      // Validate and filter files
      const validFiles = [];
      Array.from(files).forEach((file) => {
        const isImage = file.type.startsWith('image/');
        const isVideo = file.type.startsWith('video/');

        if (!isImage && !isVideo) {
          toast.error(`Formato no soportado: ${file.name}`);
          return;
        }
  
        // validate file size
        if (provider === 'google' && file.size > GOOGLE_MAX_FILE_SIZE) {
          toast.error(`El archivo supera los ${GOOGLE_MAX_FILE_SIZE / MB} MB`);
          return;
        } else if (provider === 'meta' && file.size > META_MAX_FILE_SIZE) {
          toast.error(`El archivo supera los ${META_MAX_FILE_SIZE / MB} MB`);
          return;
        }
        validFiles.push(file);
      });

      if (validFiles.length === 0) {
        setUploadingMedia(false);
        return;
      }

      // Upload all valid files in a single request
      const response = await mediaApi.uploadMedia(campaignId, validFiles);
      toast.success('Archivos subidos correctamente');
      // response.data.media could be an array of uploaded media
      const uploadedMedia = Array.isArray(response.data.media)
        ? response.data.media
        : [response.data.media];
      setMediaFiles(prev => [
        ...prev,
        ...uploadedMedia.map(media => ({
          id: media.id,
          url: media.url,
          type: 'generic'
        }))
      ]);
      // Fetch updated campaign after media upload
      if (onRefetchCampaign) {
        await onRefetchCampaign();
      } else {
        const chatId = new URLSearchParams(window.location.search).get('chat');
        if (chatId) {
          await fetchCampaign(chatId);
        }
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      toast.error(`Error al subir archivos: ${errorMessage}`);
    } finally {
      setUploadingMedia(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === "dragenter" || e.type === "dragover");
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    handleMediaUpload(files);
  };

  const handleInputChange = (e) => {
    const files = Array.from(e.target.files);
    handleMediaUpload(files);
  };

  const handleLogoInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleLogoUpload(file);
    }
  };

  const removeLogo = async (id) => {
    try {
      setLoadingLogo(true);
      await mediaApi.deleteLogo(campaignId, id);
      toast.success('Logo eliminado correctamente');
      setLogo(null);
      
      // Fetch updated campaign after logo deletion
      if (onRefetchCampaign) {
        await onRefetchCampaign();
      } else {
        const chatId = new URLSearchParams(window.location.search).get('chat');
        if (chatId) {
          await fetchCampaign(chatId);
        }
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      toast.error(`Error al eliminar el logo: ${errorMessage}`);
    } finally {
      setLoadingLogo(false);
    }
  };

  const removeMedia = async (id) => {
    try {
      setLoadingMedia(true);
      await mediaApi.deleteMedia(campaignId, id);
      toast.success('Archivo eliminado correctamente');
      
      // Update state directly instead of calling getMedia()
      setMediaFiles(prev => prev.filter(media => media.id !== id));
      
      // Fetch updated campaign after media deletion
      if (onRefetchCampaign) {
        await onRefetchCampaign();
      } else {
        const chatId = new URLSearchParams(window.location.search).get('chat');
        if (chatId) {
          await fetchCampaign(chatId);
        }
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      toast.error(`Error al eliminar el archivo: ${errorMessage}`);
    } finally {
      setLoadingMedia(false);
    }
  };

  const handleMediaError = (url, error) => {
    setMediaErrors(prev => ({
      ...prev,
      [url]: error || 'Error al cargar el medio'
    }));
    console.error(`Error con el archivo multimedia ${url}:`, error);
  };

  return {
    loadingLogo,
    loadingMedia,
    uploadingMedia,
    mediaFiles,
    logo,
    logoInputRef,
    mediaInputRef,
    dragActive,
    mediaErrors,
    setMediaErrors,
    viewMode,
    setViewMode,
    error,
    getLogo,
    getMedia,
    getAllMedia,
    handleLogoUpload,
    handleMediaUpload,
    handleDrag,
    handleDrop,
    handleInputChange,
    handleLogoInputChange,
    removeLogo,
    removeMedia,
    handleMediaError,
  };
};

export default useMedia; 